import { RodaViatura } from './viaturas/pecasViatura'
import { useDetalhesFinos } from '../ambiente/contextoQualidade'
import { CaixaArredondada } from '../componentes/CaixaArredondada'
import { COR_PADRAO_ONIBUS, DIMENSOES_ONIBUS } from '../../dominio/tabletop/onibus'

const { comprimento: C, largura: L, altura: A } = DIMENSOES_ONIBUS
const MEIO_C = C / 2
const MEIO_L = L / 2

const PRATA = '#d5dade'
const CINZA_SAIA = '#9aa3ab'
const VIDRO = '#2c3d4a'
const PRETO = '#14181d'
const PNEU = '#111418'

/** Alturas das faixas, de baixo para cima. */
const Y_SAIA = 0.3
const Y_CINTURA = 0.58
const Y_JANELA = 1.32
const Y_TOPO_JANELA = 2.62
const Y_TETO = A - 0.12

/** Eixos: balanço dianteiro curto e entre-eixos de urbano de 12 m. */
const EIXO_DIANTEIRO_Z = MEIO_C - 2.65
const EIXO_TRASEIRO_Z = EIXO_DIANTEIRO_Z - 6
const RAIO_RODA = 0.5
const MEIO_VAO_RODA = 0.66

/** Portas no lado do passageiro (-X): uma à frente do eixo dianteiro, duas atrás. */
const PORTAS: readonly (readonly [number, number])[] = [
  [EIXO_DIANTEIRO_Z + 0.75, MEIO_C - 0.25],
  [-0.5, 0.75],
  [EIXO_TRASEIRO_Z - MEIO_VAO_RODA - 1.2, EIXO_TRASEIRO_Z - MEIO_VAO_RODA - 0.05],
]

function Pintura({ cor }: { cor: string }) {
  return <meshStandardMaterial color={cor} roughness={0.5} metalness={0.15} />
}

function Vidro() {
  return <meshStandardMaterial color={VIDRO} roughness={0.18} metalness={0.35} />
}

/** Trechos da saia entre os vãos das rodas, para os pneus aparecerem por baixo. */
const TRECHOS_SAIA: readonly (readonly [number, number])[] = [
  [EIXO_DIANTEIRO_Z + MEIO_VAO_RODA, MEIO_C],
  [EIXO_TRASEIRO_Z + MEIO_VAO_RODA, EIXO_DIANTEIRO_Z - MEIO_VAO_RODA],
  [-MEIO_C, EIXO_TRASEIRO_Z - MEIO_VAO_RODA],
]

/**
 * Ônibus urbano de piso baixo, procedural, sem logo nem letreiro.
 *
 * Pintura de referência: metade de baixo na cor do objeto (verde-limão por
 * padrão), metade de cima prata, faixa de janelas escura e saia cinza. A
 * origem fica no chão, no centro: a posição final só gira e eleva o grupo.
 */
export function OnibusTabletop({ cor = COR_PADRAO_ONIBUS }: { cor?: string }) {
  const detalhes = useDetalhesFinos()
  // A metade de cima começa na linha das janelas: abaixo dela, nos vãos das
  // rodas, só devem aparecer a caixa de roda escura e o pneu.
  const baseCorpo = Y_JANELA - 0.02
  const alturaCorpo = Y_TETO - baseCorpo

  return (
    <group name="onibus">
      {/* 1. Corpo: metade superior prata e teto */}
      <mesh position={[0, baseCorpo + alturaCorpo / 2, 0]} castShadow receiveShadow>
        <CaixaArredondada args={[L, alturaCorpo, C]} raio={0.12} />
        <meshStandardMaterial color={PRATA} roughness={0.45} metalness={0.2} />
      </mesh>

      {/* 2. Metade inferior na cor do objeto, com os vãos das rodas */}
      {TRECHOS_SAIA.map(([z0, z1]) => (
        <group key={z0}>
          <mesh position={[0, (Y_CINTURA + Y_JANELA) / 2, (z0 + z1) / 2]} castShadow>
            <boxGeometry args={[L + 0.01, Y_JANELA - Y_CINTURA, z1 - z0]} />
            <Pintura cor={cor} />
          </mesh>
          <mesh position={[0, (Y_SAIA + Y_CINTURA) / 2, (z0 + z1) / 2]} castShadow>
            <boxGeometry args={[L - 0.02, Y_CINTURA - Y_SAIA, z1 - z0]} />
            <meshStandardMaterial color={CINZA_SAIA} roughness={0.6} metalness={0.2} />
          </mesh>
        </group>
      ))}
      {/* faixa na cor sobre os vãos, acima das rodas */}
      {[EIXO_DIANTEIRO_Z, EIXO_TRASEIRO_Z].map((z) => (
        <mesh key={z} position={[0, (RAIO_RODA * 2 + 0.08 + Y_JANELA) / 2, z]}>
          <boxGeometry args={[L + 0.01, Y_JANELA - RAIO_RODA * 2 - 0.08, MEIO_VAO_RODA * 2]} />
          <Pintura cor={cor} />
        </mesh>
      ))}

      {/* 3. Caixas de roda escuras e rodas */}
      {[EIXO_DIANTEIRO_Z, EIXO_TRASEIRO_Z].map((z) => (
        <group key={z} position={[0, 0, z]}>
          <mesh position={[0, (Y_SAIA + RAIO_RODA * 2 + 0.08) / 2, 0]}>
            <boxGeometry args={[L - 0.7, RAIO_RODA * 2 + 0.08 - Y_SAIA, MEIO_VAO_RODA * 2]} />
            <meshStandardMaterial color={PRETO} roughness={0.9} />
          </mesh>
          {[1, -1].map((lado) => (
            detalhes ? (
              // a mesma roda das viaturas, girada: o ônibus corre ao longo de Z
              <group key={lado} position={[lado * (MEIO_L - 0.2), RAIO_RODA, 0]} rotation={[0, Math.PI / 2, 0]}>
                <RodaViatura posicao={[0, 0, 0]} raio={RAIO_RODA} lado={lado as 1 | -1} dupla={z === EIXO_TRASEIRO_Z} />
              </group>
            ) : (
              <group key={lado} position={[lado * (MEIO_L - 0.2), RAIO_RODA, 0]} rotation={[0, 0, Math.PI / 2]}>
                <mesh castShadow>
                  <cylinderGeometry args={[RAIO_RODA, RAIO_RODA, 0.32, 12]} />
                  <meshStandardMaterial color={PNEU} roughness={0.92} />
                </mesh>
              </group>
            )
          ))}
        </group>
      ))}

      {/* 4. Faixa de janelas nas duas laterais */}
      {[1, -1].map((lado) => (
        <mesh key={lado} position={[lado * (MEIO_L + 0.006), (Y_JANELA + Y_TOPO_JANELA) / 2, -0.3]}>
          <boxGeometry args={[0.01, Y_TOPO_JANELA - Y_JANELA, C - 1.4]} />
          <Vidro />
        </mesh>
      ))}
      {detalhes && [1, -1].flatMap((lado) =>
        Array.from({ length: 8 }, (_, i) => -MEIO_C + 1.2 + i * 1.45).map((z) => (
          <mesh key={`${lado}-${z}`} position={[lado * (MEIO_L + 0.012), (Y_JANELA + Y_TOPO_JANELA) / 2, z]}>
            <boxGeometry args={[0.01, Y_TOPO_JANELA - Y_JANELA, 0.08]} />
            <meshStandardMaterial color={PRATA} roughness={0.45} metalness={0.2} />
          </mesh>
        )),
      )}

      {/* 5. Portas do lado do passageiro: vidro inteiro e vinco central */}
      {PORTAS.map(([z0, z1]) => (
        <group key={z0} position={[-(MEIO_L + 0.014), 0, (z0 + z1) / 2]}>
          <mesh position={[0, (Y_SAIA + 0.1 + Y_TOPO_JANELA) / 2, 0]}>
            <boxGeometry args={[0.012, Y_TOPO_JANELA - Y_SAIA - 0.1, z1 - z0]} />
            <meshStandardMaterial color={PRETO} roughness={0.5} />
          </mesh>
          <mesh position={[-0.004, (Y_CINTURA + 0.15 + Y_TOPO_JANELA - 0.06) / 2, 0]}>
            <boxGeometry args={[0.012, Y_TOPO_JANELA - 0.06 - Y_CINTURA - 0.15, z1 - z0 - 0.12]} />
            <Vidro />
          </mesh>
          {detalhes && (
            <mesh position={[-0.01, (Y_SAIA + Y_TOPO_JANELA) / 2, 0]}>
              <boxGeometry args={[0.012, Y_TOPO_JANELA - Y_SAIA, 0.04]} />
              <meshStandardMaterial color={PRETO} roughness={0.5} />
            </mesh>
          )}
        </group>
      ))}

      {/* 6. Frente: para-brisa grande, letreiro escuro sem texto, faróis e para-choque */}
      <group position={[0, 0, MEIO_C + 0.006]}>
        <mesh position={[0, 1.95, 0]}>
          <boxGeometry args={[L - 0.2, 1.6, 0.012]} />
          <Vidro />
        </mesh>
        <mesh position={[0, 2.93, 0]}>
          <boxGeometry args={[L - 0.5, 0.26, 0.014]} />
          <meshStandardMaterial color={PRETO} roughness={0.4} />
        </mesh>
        <mesh position={[0, 0.62, 0.03]}>
          <boxGeometry args={[L - 0.04, 0.36, 0.08]} />
          <meshStandardMaterial color={CINZA_SAIA} roughness={0.6} metalness={0.2} />
        </mesh>
        {[1, -1].map((lado) => (
          <mesh key={lado} position={[lado * 0.88, 0.98, 0.01]}>
            <boxGeometry args={[0.42, 0.14, 0.02]} />
            <meshStandardMaterial color="#f8fafc" emissive="#fef9c3" emissiveIntensity={0.6} />
          </mesh>
        ))}
        {detalhes && (
          <mesh position={[0, 1.0, 0.01]}>
            <boxGeometry args={[0.9, 0.2, 0.02]} />
            <meshStandardMaterial color={PRETO} roughness={0.7} />
          </mesh>
        )}
      </group>

      {/* 7. Traseira: vidro alto, lanternas e grade do motor */}
      <group position={[0, 0, -MEIO_C - 0.006]}>
        <mesh position={[0, 2.25, 0]}>
          <boxGeometry args={[L - 0.6, 0.75, 0.012]} />
          <Vidro />
        </mesh>
        {[1, -1].map((lado) => (
          <mesh key={lado} position={[lado * (MEIO_L - 0.18), 1.05, -0.01]}>
            <boxGeometry args={[0.18, 0.5, 0.02]} />
            <meshStandardMaterial color="#b91c1c" emissive="#7f1d1d" emissiveIntensity={0.5} />
          </mesh>
        ))}
        {detalhes && (
          <mesh position={[0, 1.15, -0.01]}>
            <boxGeometry args={[1.3, 0.55, 0.02]} />
            <meshStandardMaterial color={PRETO} roughness={0.8} />
          </mesh>
        )}
      </group>

      {/* 8. Retrovisores em haste ("orelhas") à frente das quinas superiores */}
      {detalhes && [1, -1].map((lado) => (
        <group key={lado} position={[lado * (MEIO_L - 0.05), 2.55, MEIO_C]}>
          <mesh position={[lado * 0.1, 0, 0.18]} rotation={[0.5, 0, 0]}>
            <boxGeometry args={[0.04, 0.04, 0.42]} />
            <meshStandardMaterial color={PRETO} roughness={0.5} />
          </mesh>
          <mesh position={[lado * 0.12, -0.32, 0.36]}>
            <boxGeometry args={[0.1, 0.38, 0.06]} />
            <meshStandardMaterial color={PRETO} roughness={0.5} />
          </mesh>
        </group>
      ))}

      {/* 9. Ar-condicionado no teto */}
      <mesh position={[0, Y_TETO + 0.14, -1.6]} castShadow>
        <CaixaArredondada args={[1.7, 0.26, 2.6]} raio={0.08} />
        <meshStandardMaterial color={PRATA} roughness={0.5} metalness={0.2} />
      </mesh>
    </group>
  )
}
